import { EmbeddableCatalogBrowserWithRouter } from '@tableau/data-catalog-client-components';
import * as React from 'react';
import { SiteUrlsProps } from '../app/selectors/SiteUrlsSelectors';
import { ServiceLocator } from '../app/ServiceLocator';
import { ConnectedSpaceInfoProvider } from '../app/spaces/RouterHelpers/SpaceInfoProvider';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../app/state/SpaceInfo';
import { angularProviderHOC } from '../common/react/AngularProviderHOC';
import { ReactDirectiveProps } from '../ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '../ts/FeatureFlags';
import { DatabaseDefinition, TableDefinition } from '../ts/models/ContentDefinitions';
import { DatabaseContentType, TableContentType } from '../ts/models/ContentTypes';
import { DataCatalogApiRequestsService } from '../ts/services/DataCatalogApiRequestsService';
import { DataCatalogUIActionsService } from '../ts/services/DataCatalogUIActionsService';
import { Telemetry } from '../ts/services/TelemetryService';
import { catalogApolloClient } from './CatalogApolloClient';
import { CatalogBrowserSubspaceExplorer } from './CatalogBrowserSubspaceExplorer';
import { registerErrorStore, unregisterErrorStore } from './CatalogErrorReporterService';

type CatalogBrowserHostOwnProps = ReactDirectiveProps;

export type ConnectedCatalogBrowserHostProps = SiteUrlsProps & {
  readonly areUsersVisible: boolean;
};

type CatalogBrowserHostProps =
  ConnectedCatalogBrowserHostProps &
  CatalogBrowserHostOwnProps &
  CatalogBrowserAngularProvider.PropsFromAngular;


class CatalogBrowserHost extends React.Component<CatalogBrowserHostProps> {
  private spaceInfo: SpaceInfo = {
    spaceType: 'catalog',
    subspaceName: undefined,
    item: SpaceItemForNonEntitySpace
  };

  private renderDatabasesExplorer = (routeLocationSearch: string): JSX.Element => {
    return <CatalogBrowserSubspaceExplorer
      key="database"
      subspaceName="database"
      routeLocationSearch={routeLocationSearch}
      angularContext={this.props.angularContext}
      contentDefinition={DatabaseDefinition}
      contentType={DatabaseContentType}
      telemetry={this.props.telemetry}
    />;
  }

  private renderTablesExplorer = (routeLocationSearch: string): JSX.Element => {
    return <CatalogBrowserSubspaceExplorer
      key="table"
      subspaceName="table"
      routeLocationSearch={routeLocationSearch}
      angularContext={this.props.angularContext}
      contentDefinition={TableDefinition}
      contentType={TableContentType}
      telemetry={this.props.telemetry}
    />;
  }

  public render(): JSX.Element {
    return (
      <div className="tb-fill" style={{ display: 'flex' }}>
        <ConnectedSpaceInfoProvider spaceInfo={this.spaceInfo}>
          <EmbeddableCatalogBrowserWithRouter
            client={catalogApolloClient}
            siteUrls={this.props.urls}
            apiRequests={this.props.apiRequestsService}
            uiActions={DataCatalogUIActionsService.instance()}
            renderDatabasesExplorer={this.renderDatabasesExplorer}
            renderTablesExplorer={this.renderTablesExplorer}
            registerErrorStore={registerErrorStore}
            unregisterErrorStore={unregisterErrorStore}
            newWorkbookFromCatalog={FeatureFlags.newWorkbookFromCatalog}
            areUsersVisible={this.props.areUsersVisible}
          />
        </ConnectedSpaceInfoProvider>
      </div>
    );
  }
}

namespace CatalogBrowserAngularProvider {

  export type AngularServices = {
    readonly apiRequestsService: DataCatalogApiRequestsService;
    readonly telemetry: Telemetry;
  };

  export type PropsFromAngular = AngularServices;

  function getAngularServicesWithServiceLocator() {
    const apiRequestsService = ServiceLocator.get<DataCatalogApiRequestsService>('DataCatalogApiRequestsService');
    const telemetry = ServiceLocator.get<Telemetry>('TelemetryService');
    return { apiRequestsService, telemetry };
  }

  export const HOC = angularProviderHOC<CatalogBrowserHostOwnProps & ConnectedCatalogBrowserHostProps, PropsFromAngular, AngularServices>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices: angularServices => angularServices,
  });
}

export const WrappedCatalogBrowserHost = CatalogBrowserAngularProvider.HOC(CatalogBrowserHost);
